import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Edit2, Key, Shield, ShieldCheck, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin-management/admins');
      setAdmins(response.data.admins || []);
      setCurrentAdminId(response.data.current_admin_id);
      setIsSuperAdmin(response.data.is_super_admin);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Fehler beim Laden der Admin-Liste');
    } finally {
      setLoading(false);
    }
  };

  const handleMakeSuperAdmin = async () => {
    try {
      await axiosInstance.post('/admin-management/make-super-admin');
      toast.success('Sie sind jetzt der Haupt-Admin!');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/admin-management/admins', formData);
      toast.success('Admin erfolgreich erstellt!');
      setShowAddDialog(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Admin wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    try {
      await axiosInstance.delete(`/admin-management/admins/${adminId}`);
      toast.success('Admin gelöscht');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await axiosInstance.put(`/admin-management/admins/${admin.id}`, {
        is_active: !admin.is_active
      });
      toast.success(admin.is_active ? 'Admin deaktiviert' : 'Admin aktiviert');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!selectedAdmin) return;
    try {
      await axiosInstance.put(`/admin-management/admins/${selectedAdmin.id}/password`, {
        new_password: newPassword
      });
      toast.success('Passwort geändert!');
      setShowPasswordDialog(false);
      setNewPassword('');
      setSelectedAdmin(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-gray-700 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin-Verwaltung</h1>
          </div>
          
          {!isSuperAdmin && (
            <Button 
              onClick={handleMakeSuperAdmin}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-none w-full sm:w-auto"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Als Haupt-Admin festlegen
            </Button>
          )}
          
          {isSuperAdmin && (
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0 w-full sm:w-auto"
              data-testid="add-admin-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Neuen Admin hinzufügen
            </Button>
          )}
        </div>

        {/* Info Box */}
        <Card className="mb-4 rounded-none border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Haupt-Admin Rechte</p>
                <p className="text-sm text-gray-600">
                  Nur der Haupt-Admin kann neue Admins erstellen, bearbeiten oder löschen. 
                  {isSuperAdmin ? ' Sie sind der Haupt-Admin.' : ' Sie sind kein Haupt-Admin.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admins List */}
        <Card className="rounded-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Alle Administratoren ({admins.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <p className="text-gray-600 text-center py-8">Keine Admins gefunden</p>
            ) : (
              <div className="space-y-3">
                {admins.map((admin) => {
                  // Treat missing is_active as active (legacy data)
                  const isActive = admin.is_active !== false;
                  return (
                  <div 
                    key={admin.id} 
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border ${
                      admin.id === currentAdminId ? 'bg-amber-50 border-amber-200' : 'bg-white'
                    } ${!isActive ? 'opacity-60' : ''}`}
                    data-testid={`admin-row-${admin.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
                        admin.is_super_admin ? 'bg-amber-100' : 'bg-gray-100'
                      }`}>
                        {admin.is_super_admin ? (
                          <ShieldCheck className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Users className="h-5 w-5 text-gray-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-medium text-gray-900 truncate">
                            {admin.first_name || admin.name || 'Admin'} {admin.last_name || ''}
                          </p>
                          {admin.is_super_admin && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 font-medium whitespace-nowrap">
                              HAUPT-ADMIN
                            </span>
                          )}
                          {admin.id === currentAdminId && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 whitespace-nowrap">
                              Sie
                            </span>
                          )}
                          {!isActive && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-800 whitespace-nowrap">
                              Deaktiviert
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 break-all sm:truncate">{admin.email}</p>
                      </div>
                    </div>
                    
                    {isSuperAdmin && !admin.is_super_admin && (
                      <div className="flex items-center gap-2 flex-shrink-0 sm:justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive({ ...admin, is_active: isActive })}
                          className="rounded-none flex-1 sm:flex-initial"
                          title={isActive ? 'Deaktivieren' : 'Aktivieren'}
                          data-testid={`admin-toggle-active-${admin.id}`}
                        >
                          {isActive ? (
                            <X className="h-4 w-4" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedAdmin(admin);
                            setShowPasswordDialog(true);
                          }}
                          className="rounded-none flex-1 sm:flex-initial"
                          title="Passwort neu setzen"
                          data-testid={`admin-reset-password-${admin.id}`}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50 flex-1 sm:flex-initial"
                          title="Löschen"
                          data-testid={`admin-delete-${admin.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Neuen Admin hinzufügen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">E-Mail</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="rounded-none"
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Passwort</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={8}
                className="rounded-none"
                placeholder="Mindestens 8 Zeichen"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Vorname</label>
                <Input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  className="rounded-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nachname</label>
                <Input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="rounded-none border-[#D4C4B0]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setShowAddDialog(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">
                Abbrechen
              </Button>
              <Button type="submit" className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                Admin erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-none" style={{ backgroundColor: '#F5EDE4' }}>
          <DialogHeader>
            <DialogTitle className="text-[#5D4E3C]">Passwort ändern für {selectedAdmin?.email}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-[#5D4E3C]">Neues Passwort</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="rounded-none border-[#D4C4B0]"
                placeholder="Mindestens 8 Zeichen"
              />
            </div>
            <DialogFooter>
              <Button type="button" onClick={() => setShowPasswordDialog(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">
                Abbrechen
              </Button>
              <Button type="submit" className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                Passwort ändern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
